import React, { useState, useEffect } from 'react';
import { sendApiRequest } from '../../utils';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const WithdrawalRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stats, setStats] = useState({
    counts: {
      total: 0,
      pending: 0,
      approved: 0,
      rejected: 0
    },
    amounts: {
      total: 0,
      pending: 0,
      approved: 0,
      rejected: 0
    }
  });
  const [searchParams, setSearchParams] = useState({
    status: 'all',
    startDate: null,
    endDate: null
  });

  const fetchStats = async () => {
    try {
      const response = await sendApiRequest('api/withdrawal-requests/admin/stats', {}, 'GET');
      if (response.status === 200) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        ...(searchParams.status !== 'all' && { status: searchParams.status }),
        ...(searchParams.startDate && { startDate: searchParams.startDate.toISOString() }),
        ...(searchParams.endDate && { endDate: searchParams.endDate.toISOString() })
      });

      const response = await sendApiRequest(`api/withdrawal-requests/admin?${params}`, {}, 'GET');
      
      if (response.status === 200) {
        setRequests(response.data.withdrawalRequests || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalRecords(response.data.total || 0);
      } else {
        toast.error(response.data?.message || 'Failed to fetch withdrawal requests');
        setRequests([]);
        setTotalPages(1);
        setTotalRecords(0);
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred while fetching requests');
      setRequests([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, [currentPage, itemsPerPage, searchParams]);

  const handleStatusUpdate = async (requestId, status, adminNote) => {
    try {
      const response = await sendApiRequest(`api/withdrawal-requests/admin/${requestId}`, {status, adminNote}, 'PUT');

      if (response.status === 200) {
        toast.success('Withdrawal request status updated successfully');
        fetchRequests();
        setIsModalOpen(false);
      } else {
        toast.error(response.data?.message || 'Failed to update request status');
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred while updating the request');
    }
  };

  const handleSearch = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleDateChange = (date, field) => {
    setSearchParams(prev => ({ ...prev, [field]: date }));
    setCurrentPage(1);
  };

  const openModal = (request) => {
    setSelectedRequest(request);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedRequest(null);
    setIsModalOpen(false);
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Withdrawal Requests Management</h1>

      {/* Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-lg font-semibold mb-2">Total Requests</h3>
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-2xl font-bold text-blue-600">{stats.counts.total}</span>
              <span className="text-lg text-gray-600">₹{stats.amounts.total.toLocaleString()}</span>
            </div>
            <div className="text-sm text-gray-500">
              Admin Fee: ₹{(stats.amounts.total * 0.05).toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">
              TDS: ₹{(stats.amounts.total * 0.05).toLocaleString()}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-lg font-semibold mb-2">Pending</h3>
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-2xl font-bold text-yellow-600">{stats.counts.pending}</span>
              <span className="text-lg text-gray-600">₹{stats.amounts.pending.toLocaleString()}</span>
            </div>
            <div className="text-sm text-gray-500">
              Admin Fee: ₹{(stats.amounts.pending * 0.05).toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">
              TDS: ₹{(stats.amounts.pending * 0.05).toLocaleString()}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-lg font-semibold mb-2">Approved</h3>
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-2xl font-bold text-green-600">{stats.counts.approved}</span>
              <span className="text-lg text-gray-600">₹{stats.amounts.approved.toLocaleString()}</span>
            </div>
            <div className="text-sm text-gray-500">
              Admin Fee: ₹{(stats.amounts.approved * 0.05).toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">
              TDS: ₹{(stats.amounts.approved * 0.05).toLocaleString()}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-lg font-semibold mb-2">Rejected</h3>
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-2xl font-bold text-red-600">{stats.counts.rejected}</span>
              <span className="text-lg text-gray-600">₹{stats.amounts.rejected.toLocaleString()}</span>
            </div>
            <div className="text-sm text-gray-500">
              Admin Fee: ₹{(stats.amounts.rejected * 0.05).toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">
              TDS: ₹{(stats.amounts.rejected * 0.05).toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Search Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              name="status"
              value={searchParams.status}
              onChange={handleSearch}
              className="w-full p-2 border rounded-md"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <DatePicker
              selected={searchParams.startDate}
              onChange={(date) => handleDateChange(date, 'startDate')}
              className="w-full p-2 border rounded-md"
              dateFormat="yyyy-MM-dd"
              placeholderText="Select start date"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <DatePicker
              selected={searchParams.endDate}
              onChange={(date) => handleDateChange(date, 'endDate')}
              className="w-full p-2 border rounded-md"
              dateFormat="yyyy-MM-dd"
              placeholderText="Select end date"
            />
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="mb-4">
        <p className="text-gray-600">
          Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
          {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords} entries
        </p>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Details</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Details</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center">
                    Loading...
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center">
                    No withdrawal requests found
                  </td>
                </tr>
              ) : (
                requests.map((request) => (
                  <tr key={request._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div className="font-medium">{request.userId?.fullName}</div>
                        <div className="text-gray-500">{request.userId?.email}</div>
                        <div className="text-gray-500">{request.userId?.phone}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900">
                          Requested: ₹{request.amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-xs text-gray-500">
                          Admin Fee (5%): ₹{request.deductions.adminFee.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-xs text-gray-500">
                          TDS (5%): ₹{request.deductions.tds.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-sm font-semibold text-green-600">
                          Net Amount: ₹{request.deductions.netAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-500 w-24">Bank Name:</span>
                            <span className="font-medium text-gray-900">{request.bankDetails?.bankName}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-500 w-24">Account No:</span>
                            <span className="font-medium text-gray-900">{request.bankDetails?.accountNumber}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-500 w-24">IFSC Code:</span>
                            <span className="font-medium text-gray-900">{request.bankDetails?.ifscCode}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-500 w-24">Branch:</span>
                            <span className="font-medium text-gray-900">{request.bankDetails?.branchName}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-500 w-24">Type:</span>
                            <span className="font-medium text-gray-900">{request.bankDetails?.accountType}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(request.createdAt).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(request.createdAt).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          request.status === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : request.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openModal(request)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex justify-between items-center">
        <div className="flex items-center">
          <span className="mr-2">Show</span>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="p-1 border rounded"
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
          <span className="ml-2">entries</span>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-3 py-1">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* Status Update Modal */}
      {isModalOpen && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Update Withdrawal Request</h3>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">Requested Amount: ₹{selectedRequest.amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
                  <p className="text-sm text-gray-500">Admin Fee (5%): ₹{selectedRequest.deductions.adminFee.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
                  <p className="text-sm text-gray-500">TDS (5%): ₹{selectedRequest.deductions.tds.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
                  <p className="text-sm font-semibold text-green-600">Net Amount: ₹{selectedRequest.deductions.netAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
                  <p className="text-sm text-gray-600">Current Status: {selectedRequest.status}</p>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">New Status</label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={selectedRequest.status}
                  onChange={(e) => setSelectedRequest(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Note</label>
                <textarea
                  className="w-full p-2 border rounded-md"
                  rows="3"
                  value={selectedRequest.adminNote || ''}
                  onChange={(e) => setSelectedRequest(prev => ({ ...prev, adminNote: e.target.value }))}
                  placeholder="Enter any notes for this request"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 border rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleStatusUpdate(selectedRequest._id, selectedRequest.status, selectedRequest.adminNote)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalRequests; 